

import React, { useEffect, useRef, useState } from "react";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { IconButton } from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import toast from "react-hot-toast";
import LocationHelperModal from "./Locationhelper";
import { useTranslation } from "react-i18next";
import { GoogleMap, Marker, Circle, useJsApiLoader } from "@react-google-maps/api";
import { MAPS_API_KEY } from "./mapKey";
import { getPositionWithFallback, useLocationPermissionSync } from "../utils/getPositionWithFallback";

const DEFAULT_COORDS = { lat: 33.6844, lng: 73.0479 }; // fallback Islamabad
const containerStyle = {
  width: "100%",
  height: "100%",
};

const LocationPicker = ({ formik, height = "400px", width = "100%", initialPosition = null }) => {
  const mapRef = useRef(null);
  const fullMapRef = useRef(null);
  const inlineMarkerRef = useRef(null);
  const fullMarkerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const searchInputRef = useRef(null);
  const { t } = useTranslation();
  const isOnline = () => navigator.onLine;
  const geolocationCanceledRef = useRef(false);
  const [position, setPosition] = useState(DEFAULT_COORDS);
  const [address, setAddress] = useState("");
  const [showFullMap, setShowFullMap] = useState(false);
  const [isProcessingLocation, setIsProcessingLocation] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const searchTimeoutRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: MAPS_API_KEY,
    libraries: ["places"],
  });

  // Monitor formik values
  useEffect(() => {
    if (formik?.values) {
      console.log("📍 LocationPicker - Formik values updated:", {
        country: formik.values.country,
        city: formik.values.city,
        province: formik.values.province,
      });
    }
  }, [formik?.values?.country, formik?.values?.city, formik?.values?.province]);

  // Add CSS for spinner animation
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // When editing a saved record, use its stored coordinates instead of geolocation
  useEffect(() => {
    if (!isLoaded || !initialPosition?.lat || !initialPosition?.lng) return;
    geolocationCanceledRef.current = true;
    setPosition({ lat: initialPosition.lat, lng: initialPosition.lng });
    // Build address display from existing formik values — avoids re-geocoding and overwriting saved fields
    const { street_address, city, province, country } = formik?.values || {};
    const parts = [street_address, city, province, country].filter(Boolean);
    if (parts.length > 0) setAddress(parts.join(", "));
  }, [isLoaded, initialPosition?.lat, initialPosition?.lng]);

  // Reverse geocode & update formik + address state
  const fetchAddress = async ({ lat, lng }) => {
    if (!isLoaded) return;

    if (!isOnline()) {
      toast.error(t("locationInternetError") || "No internet connection");
      return;
    }

    setIsProcessingLocation(true);
    try {
      console.log("🌍 Fetching address for:", lat, lng);

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          const formattedAddress = results[0].formatted_address;
          const addressComponents = results[0].address_components;

          console.log("🗺️ Reverse Geocoding Result:", results[0]);

          let country = "";
          let province = "";
          let city = "";
          let community = "";
          let postalCode = "";
          let streetAddress = "";
          let countryCode = "";

          // Parse address components
          addressComponents.forEach((component) => {
            const types = component.types;

            if (types.includes("country")) {
              country = component.long_name;
              countryCode = component.short_name;
            }

            if (types.includes("administrative_area_level_1")) {
              province = component.long_name;
            }

            if (types.includes("locality") || types.includes("postal_town")) {
              city = component.long_name;
            }

            if (types.includes("sublocality") || types.includes("neighborhood")) {
              community = component.long_name;
            }

            if (types.includes("administrative_area_level_2")) {
              if (!community) community = component.long_name;
            }

            if (types.includes("postal_code")) {
              postalCode = component.long_name;
            }

            if (types.includes("route")) {
              streetAddress = component.long_name;
            }

            if (types.includes("street_number")) {
              streetAddress = component.long_name + " " + streetAddress;
            }
          });

          // If city not found, try alternative
          if (!city) {
            addressComponents.forEach((component) => {
              if (component.types.includes("administrative_area_level_2")) {
                city = component.long_name;
              }
            });
          }

          console.log("📝 Setting formik values:", {
            country,
            province,
            city,
            community,
            postalCode,
            streetAddress,
            latitude: lat,
            longitude: lng,
          });

          setAddress(formattedAddress);

          if (formik?.setFieldValue) {
            formik.setFieldValue("country", country || "");
            formik.setFieldValue("province", province || "");
            formik.setFieldValue("city", city || "");
            formik.setFieldValue("community", community || "");
            formik.setFieldValue("postal_code", postalCode || "");
            formik.setFieldValue("street_address", streetAddress || "");
            // Forms that use `street` for the API (e.g. Add Account Executive) read this field, not `street_address`
            formik.setFieldValue("street", streetAddress || "");
            formik.setFieldValue("latitude", lat);
            formik.setFieldValue("longitude", lng);

            // Trigger formik validation
            formik.setFieldTouched("country", true);
            formik.setFieldTouched("city", true);

            console.log("✅ Location data successfully set in formik");
          }
        } else {
          console.error("❌ Geocoding failed:", status);
          setAddress("Address not found");

          if (formik?.setFieldValue) {
            formik.setFieldValue("latitude", lat);
            formik.setFieldValue("longitude", lng);
          }
        }
        setIsProcessingLocation(false);
      });
    } catch (err) {
      console.error("❌ Reverse geocoding failed:", err);
      setIsProcessingLocation(false);

      if (formik?.setFieldValue) {
        formik.setFieldValue("latitude", lat);
        formik.setFieldValue("longitude", lng);
      }

      setAddress(`Location: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    }
  };

  // Initialize maps and geolocation
  useEffect(() => {
    if (!isLoaded) return;

    // Initialize Google Places services
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    placesServiceRef.current = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );

    // Try to get current position (skipped when a saved position is provided)
    if (navigator.geolocation) {
      console.log("🌍 Attempting to get current position...");
      setIsProcessingLocation(true);
      getPositionWithFallback(
        (pos) => {
          // If initialPosition arrived while geolocation was in-flight, discard this result
          if (geolocationCanceledRef.current) {
            setIsProcessingLocation(false);
            return;
          }
          console.log("📍 Current position obtained:", pos.coords);
          const newPos = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          setPosition(newPos);
          fetchAddress(newPos);
        },
        (err) => {
          console.log("❌ Geolocation error:", err);
          if (err.code === err.PERMISSION_DENIED) {
            console.log("📍 Location permission denied, using default coordinates");
            setOpen(true);
          }
          setIsProcessingLocation(false);
        }
      );
    }
  }, [isLoaded]);

  // Handle marker drag
  const handleMarkerDragEnd = (e) => {
    const newPos = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    setPosition(newPos);
    fetchAddress(newPos);
  };

  // Handle map click
  const handleMapClick = (e) => {
    const newPos = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    setPosition(newPos);
    fetchAddress(newPos);
  };

  // Fetch location suggestions with debouncing
  const fetchSuggestions = async (query) => {
    if (!query || query.trim().length < 2 || !isLoaded) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (!isOnline()) {
      toast.error(t("locationInternetError") || "No internet connection");
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoadingSuggestions(true);
    try {
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        { input: query },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            setSuggestions(predictions);
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
            setShowSuggestions(false);
          }
          setIsLoadingSuggestions(false);
        }
      );
    } catch (err) {
      console.error("Error fetching suggestions:", err);
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoadingSuggestions(false);
    }
  };

  // Debounced search handler
  const handleSearchInputChange = (e) => {
    const query = e.target.value;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchSuggestions(query);
    }, 500);
  };

  // Handle search (for Enter key or button click)
  const handleSearch = async () => {
    const q = searchInputRef.current?.value?.trim();
    if (!q || !isLoaded) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (!isOnline()) {
      toast.error(t("locationInternetError") || "No internet connection");
      return;
    }

    setIsLoadingSuggestions(true);
    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: q }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          const newPos = {
            lat: location.lat(),
            lng: location.lng(),
          };
          setPosition(newPos);
          fetchAddress(newPos);
          setAddress(results[0].formatted_address);
          setShowSuggestions(false);
          setSuggestions([]);
        } else {
          toast.error("No location found.");
        }
        setIsLoadingSuggestions(false);
      });
    } catch (err) {
      console.error("Search error:", err);
      toast.error("Something went wrong! Please try again.");
      setIsLoadingSuggestions(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    if (!isLoaded) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: suggestion.place_id }, (results, status) => {
      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;
        const newPos = {
          lat: location.lat(),
          lng: location.lng(),
        };
        setPosition(newPos);
        fetchAddress(newPos);
        setAddress(results[0].formatted_address);
        setShowSuggestions(false);
        setSuggestions([]);
        if (searchInputRef.current) {
          searchInputRef.current.value = results[0].formatted_address;
        }
      }
    });
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleOpenFullMap = (e) => {
    if (e) e.preventDefault();
    setShowFullMap(true);
  };

  const goToCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser.");
      return;
    }
    if (!isOnline()) {
      toast.error(t("locationInternetError") || "No internet connection. Please check your connection.");
      return;
    }
    setIsProcessingLocation(true);
    getPositionWithFallback(
      (pos) => {
        console.log("📍 Current position obtained:", pos.coords);
        const newPos = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        setPosition(newPos);
        fetchAddress(newPos);
        toast.success(t("Location_updated"));
        setIsProcessingLocation(false);
      },
      (err) => {
        console.error("❌ Geolocation error:", err);
        setIsProcessingLocation(false);
        if (err.code === err.PERMISSION_DENIED) {
          setOpen(true);
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          toast.error("Location information unavailable.");
        } else if (err.code === err.TIMEOUT) {
          toast.error("Location request timed out. Please try again.");
        } else {
          toast.error("Something went wrong getting your location. Please try again.");
        }
      }
    );
  };

  // Reflect Allow/Block changes made in the browser while this picker is open
  useLocationPermissionSync({
    onGranted: () => {
      if (open) {
        setOpen(false);
        goToCurrentLocation();
      }
    },
    onDenied: () => setOpen(true),
  });

  // Monitor internet connectivity
  useEffect(() => {
    const handleOnline = () => {
      toast.success(t("internetRestored") || "Internet connection restored");
      // Retry fetching address if coordinates exist
      if (position?.lat && position?.lng) {
        fetchAddress(position);
      }
    };

    const handleOffline = () => {
      toast.error(t("internetConnectionLost") || "Internet connection lost");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [position]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading  Maps...</div>;


  return (
    <div style={{ marginBottom: 20 }}>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          flexDirection: window.innerWidth < 600 ? "column" : "row",
          gap: 8,
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <div style={{ position: "relative", flex: 1, width: window.innerWidth < 600 ? "100%" : "auto" }}>
          <input
            ref={searchInputRef}
            id="location-search"
            type="text"
            placeholder={t("Search location...")}
            onKeyDown={onSearchKeyDown}
            onChange={handleSearchInputChange}
            onFocus={() => {
              if (suggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            onBlur={() => {
              setTimeout(() => setShowSuggestions(false), 200);
            }}
            style={{
              width: "100%",
              padding: window.innerWidth < 600 ? "8px 12px" : "6px 8px",
              border: "1px solid #ccc",
              borderRadius: 6,
              fontSize: window.innerWidth < 600 ? 16 : 14,
            }}
          />
          {/* Suggestions Dropdown */}
          {showSuggestions && (suggestions.length > 0 || isLoadingSuggestions) && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: "white",
                border: "1px solid #ccc",
                borderRadius: 6,
                marginTop: 4,
                maxHeight: "300px",
                overflowY: "auto",
                zIndex: 1000,
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              }}
            >
              {isLoadingSuggestions ? (
                <div style={{ padding: "12px", textAlign: "center", color: "#666" }}>
                  Searching...
                </div>
              ) : (
                suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    style={{
                      padding: "12px",
                      cursor: "pointer",
                      borderBottom: index < suggestions.length - 1 ? "1px solid #eee" : "none",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f5f5f5";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "white";
                    }}
                  >
                    <div style={{ fontWeight: 500, fontSize: "14px", color: "#333" }}>
                      {suggestion.description}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                      {suggestion.secondary_text}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            width: window.innerWidth < 600 ? "100%" : "auto",
            justifyContent: window.innerWidth < 600 ? "space-between" : "flex-start",
          }}
        >
          <IconButton
            onClick={handleSearch}
            style={{
              padding: window.innerWidth < 600 ? "8px 12px" : "6px 10px",
              border: "none",
              background: "#007bff",
              color: "white",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: window.innerWidth < 600 ? 16 : 14,
            }}
          >
            <SearchOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={goToCurrentLocation}
            style={{
              padding: window.innerWidth < 600 ? "8px 12px" : "6px 10px",
              border: "none",
              background: "#007bff",
              color: "white",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: window.innerWidth < 600 ? 16 : 14,
            }}
          >
            <LocationOnOutlinedIcon />
          </IconButton>

          <button
            type="button"
            onClick={handleOpenFullMap}
            style={{
              padding: window.innerWidth < 600 ? "8px 16px" : "6px 12px",
              border: "1px solid #007bff",
              background: "white",
              color: "#007bff",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: window.innerWidth < 600 ? 16 : 14,
              fontWeight: 500,
            }}
          >
            {t("fields.fullMap")}
          </button>
        </div>
      </div>

      {/* Inline Google Map */}
      <div
        style={{
          width,
          height,
          marginBottom: 8,
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={position}
            zoom={15}
            onClick={handleMapClick}
          >
            <Marker
              position={position}
              draggable
              onDragEnd={handleMarkerDragEnd}
            />
            <Circle
              center={position}
              radius={100}
              options={{
                fillColor: "#3f51b5",
                fillOpacity: 0.2,
                strokeColor: "#3f51b5",
              }}
            />
          </GoogleMap>
        )}
      </div>

      {/* Address box */}
      <div
        style={{
          minHeight: 50,
          padding: "8px 12px",
          background: "#f5f5f5",
          borderRadius: 6,
          border: "1px solid #ddd",
          fontSize: 14,
          whiteSpace: "normal",
          wordWrap: "break-word",
        }}
      >
        {isProcessingLocation ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "16px",
                height: "16px",
                border: "2px solid #ccc",
                borderTop: "2px solid #006EC2",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            {t("fields.processingLocation")}
          </div>
        ) : (
          address || t("fields.pickALocation")
        )}
      </div>

      {/* Full map modal */}
      {showFullMap && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "white",
            zIndex: 2000,
            borderRadius: window.innerWidth < 600 ? 0 : 8,
            overflow: "hidden",
            boxShadow: window.innerWidth < 600 ? "none" : "0 6px 30px rgba(0,0,0,0.35)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              padding: window.innerWidth < 600 ? 12 : 8,
              textAlign: "right",
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #ddd",
            }}
          >
            <IconButton
              onClick={() => setShowFullMap(false)}
              style={{
                color: "black",
                cursor: "pointer",
                fontSize: window.innerWidth < 600 ? 24 : 20,
              }}
            >
              <CloseOutlinedIcon />
            </IconButton>
          </div>
          {isLoaded && (
            <div style={{ flex: 1 }}>
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={position}
                zoom={15}
                onClick={handleMapClick}
              >
                <Marker
                  position={position}
                  draggable
                  onDragEnd={handleMarkerDragEnd}
                />
                <Circle
                  center={position}
                  radius={100}
                  options={{
                    fillColor: "#3f51b5",
                    fillOpacity: 0.2,
                    strokeColor: "#3f51b5",
                  }}
                />
              </GoogleMap>
            </div>
          )}
        </div>
      )}

      {/* Location permission helper */}
      <LocationHelperModal
        open={open}
        onClose={() => setOpen(false)}
        onRefresh={() => {
          window.location.reload();
        }}
        onManual={() => {
          setOpen(false);
        }}
      />
    </div>
  );
};

export default LocationPicker;
